import { ShallowCourse } from "@vixi/types";
import { useTranslation } from "@vixi/localization";
import { cn } from "@/lib";
import { DropdownMenu, DropdownMenuTrigger } from "@radix-ui/react-dropdown-menu";
import { Button } from "../base/button";
import { ActionMenuContent, ActionMenuItem } from "../base/dropdown-menu";
import { Img } from "../base/img";
import { Skeleton } from "../base/skeleton";
import { UnstyledLink } from "../unstyled/link";
import { COURSE_STATUS, COURSE_STATUS_LABELS } from "@vixi/types";

// Assets
import Dots2VerticalIcon from "@assets/icons/dots-2-vertical.svg?react";

// Constants
const min_card_height = "min-h-49.75";

interface CourseCardProps {
  loading?: boolean;
  course?: ShallowCourse;
  image?: string;
  premium?: boolean;
  price?: number;
  disableLink?: boolean;
}

export const CourseCard = ({ course, loading, image, disableLink }: CourseCardProps) => {
  const { t } = useTranslation();

  if (loading || !course) return <Skeleton className={cn("w-full rounded", min_card_height)} />;

  const { id: courseId, title } = course ?? {};
  return (
    <div
      className={`bg-card hover:border-primary relative flex h-full flex-col overflow-hidden rounded border-2 border-transparent transition-all duration-300 ${min_card_height}`}
    >
      <UnstyledLink
        disabled={disableLink}
        to="/courses/$courseId"
        params={{ courseId: courseId.toString() }}
        className="relative"
      >
        <Img className="mx-auto h-33.75 object-center p-1.5" src={image} />
        {course && (
          <h3
            className={cn(
              "absolute top-[-12px] right-[12px] mx-auto mt-7.5 w-fit rounded-md px-4 py-1 text-center text-[15px] font-semibold",
              {
                [COURSE_STATUS.DRAFT]: "bg-gray-200",
                [COURSE_STATUS.PUBLISHED]: "text-success bg-success-light",
                [COURSE_STATUS.WAITING_PUBLISH]: "bg-yellow-100 text-yellow-600",
                [COURSE_STATUS.WAITING_UNPUBLISH]: "bg-red-100 text-red-600",
              }[course.courseStatus],
            )}
          >
            {!!course ? t(COURSE_STATUS_LABELS[course.courseStatus]) : ""}
          </h3>
        )}
      </UnstyledLink>

      <div className="border-primary-light flex items-start gap-1.25 border-t-2 ps-4.75 pe-3.75 pt-1.5 pb-2.5">
        <UnstyledLink
          disabled={disableLink}
          to="/courses/$courseId"
          params={{ courseId: courseId.toString() }}
          className="flex-1"
        >
          <h5 className="fw-600 text-foreground leading-[1.2] no-underline">{title}</h5>
        </UnstyledLink>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="icon" variant="text" color="muted">
              <Dots2VerticalIcon />
            </Button>
          </DropdownMenuTrigger>
          <ActionMenuContent>
            <ActionMenuItem>
              <button>{t("Action.Rename")}</button>
            </ActionMenuItem>
            <ActionMenuItem disabled={course.courseStatus !== COURSE_STATUS.PUBLISHED}>
              <button>{t("Action.Unpublish")}</button>
            </ActionMenuItem>
          </ActionMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};
